import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, query, orderBy, doc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Pencil, Trash2, Plus } from 'lucide-react';
import { EditArticleModal } from '../modals/EditArticleModal';
import { AddArticleModal } from '../modals/AddArticleModal';
import { useLanguage } from '@/contexts/LanguageContext';
import { toArabicNumerals } from '@/lib/utils';

interface Article {
  id: string;
  title: { ar: string; en: string; } | string; // Support both old and new format
  excerpt: { ar: string; en: string; } | string;
  content: { ar: string; en: string; } | string;
  author: { ar: string; en: string; } | string; // Support both old and new format
  category: { ar: string; en: string; } | string; // Support both old and new format
  image_url: string;
  read_time: number;
  created_at: string;
}

export function ArticlesSection() {
  const { language } = useLanguage();
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const articlesQuery = query(
        collection(db, 'landing_articles'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(articlesQuery);

      const articlesData = querySnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          title: data.title || { ar: '', en: '' },
          excerpt: data.excerpt || { ar: '', en: '' },
          content: data.content || { ar: '', en: '' },
          author: data.author || '',
          category: data.category || '',
          image_url: data.image_url || '',
          read_time: data.read_time || 0,
          created_at: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
        };
      }) as Article[];

      setArticles(articlesData);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('حدث خطأ أثناء تحميل المقالات');
    } finally {
      setIsLoadingArticles(false);
    }
  };

  const handleEditArticle = (article: Article) => {
    setSelectedArticle(article);
    setIsEditModalOpen(true);
  };

  const handleSaveArticle = async (updatedArticle: Article) => {
    try {
      const articleRef = doc(db, 'landing_articles', updatedArticle.id);
      await updateDoc(articleRef, {
        title: updatedArticle.title,
        excerpt: updatedArticle.excerpt,
        content: updatedArticle.content,
        author: updatedArticle.author,
        category: updatedArticle.category,
        image_url: updatedArticle.image_url,
        read_time: updatedArticle.read_time,
        updatedAt: new Date()
      });

      toast.success('تم تحديث المقال بنجاح');
      fetchArticles();
    } catch (error) {
      console.error('Error updating article:', error);
      toast.error('حدث خطأ أثناء تحديث المقال');
      throw error;
    }
  };

  const handleAddArticle = async (newArticle: Omit<Article, 'id' | 'created_at'>) => {
    try {
      await addDoc(collection(db, 'landing_articles'), {
        ...newArticle,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      toast.success('تم إضافة المقال بنجاح');
      fetchArticles();
    } catch (error) {
      console.error('Error adding article:', error);
      toast.error('حدث خطأ أثناء إضافة المقال');
      throw error;
    }
  };

  const handleDeleteArticle = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'landing_articles', id));

      toast.success('تم حذف المقال بنجاح');
      setShowDeleteConfirm(null);
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('حدث خطأ أثناء حذف المقال');
    }
  };

  const formatDate = (date: string) => {
    const formatted = new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    // Convert to Arabic-Indic numerals if in Arabic mode
    return language === 'ar' ? toArabicNumerals(formatted) : formatted;
  };

  if (isLoadingArticles) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold">إدارة المقالات</h3>
        <Button onClick={() => setIsAddModalOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          إضافة مقال جديد
        </Button>
      </div>

      <div className="space-y-4">
        {articles.map((article) => {
          const articleTitle = typeof article.title === 'string' ? article.title : (article.title[language] || article.title.ar);
          const articleExcerpt = typeof article.excerpt === 'string' ? article.excerpt : (article.excerpt[language] || article.excerpt.ar);
          
          // Safe rendering for author and category with proper language selection
          const author = typeof article.author === 'string' 
            ? article.author 
            : (typeof article.author === 'object' ? (article.author[language] || article.author.ar || article.author.en || '') : '');
          const category = typeof article.category === 'string' 
            ? article.category 
            : (typeof article.category === 'object' ? (article.category[language] || article.category.ar || article.category.en || '') : '');

          return (
            <div key={article.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <h4 className="font-semibold text-lg mb-2">{articleTitle}</h4>
                  <p className="text-muted-foreground mb-2 line-clamp-2">{articleExcerpt}</p>
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <span>الكاتب: {author}</span>
                    <span>التصنيف: {category}</span>
                    <span>التاريخ: {formatDate(article.created_at)}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditArticle(article)}
                    className="gap-2"
                  >
                    <Pencil className="h-4 w-4" />
                    تعديل
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(article.id)}
                    className="gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    حذف
                  </Button>
                </div>
              </div>

              {/* Delete Confirmation */}
              {showDeleteConfirm === article.id && (
                <div className="mt-4 p-4 bg-destructive/10 rounded-lg">
                  <p className="text-destructive mb-3">هل أنت متأكد من حذف هذا المقال؟</p>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(null)}
                    >
                      إلغاء
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteArticle(article.id)}
                    >
                      تأكيد الحذف
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {articles.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            لا توجد مقالات حالياً
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {selectedArticle && (
        <EditArticleModal
          article={selectedArticle}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedArticle(null);
          }}
          onSave={handleSaveArticle}
        />
      )}

      {/* Add Modal */}
      <AddArticleModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddArticle}
      />
    </Card>
  );
}

